import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileFrame from '@/components/MobileFrame';
import { Icon } from '@/components/Icons';
import { tpl, useT } from '@/lib/i18n';
import { useOnboardingStore } from '@/lib/store';
import { dataUrlToBlob, submitSignature } from '@/lib/onboarding-api';

export default function SignatureScreen() {
    const t = useT();
    const navigate = useNavigate();
    const setSignatureDataUrl = useOnboardingStore((s) => s.setSignatureDataUrl);
    const setVerification = useOnboardingStore((s) => s.setVerification);

    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawingRef = useRef(false);
    const lastRef = useRef<{ x: number; y: number } | null>(null);
    const [hasDrawn, setHasDrawn] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        const dpr = window.devicePixelRatio || 1;
        c.width = c.offsetWidth * dpr;
        c.height = c.offsetHeight * dpr;
        const ctx = c.getContext('2d');
        if (!ctx) return;
        ctx.scale(dpr, dpr);
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#0F1419';
    }, []);

    const getPoint = (
        e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
    ) => {
        const c = canvasRef.current;
        if (!c) return { x: 0, y: 0 };
        const rect = c.getBoundingClientRect();
        if ('touches' in e) {
            return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
        }
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const start = (
        e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
    ) => {
        e.preventDefault();
        drawingRef.current = true;
        lastRef.current = getPoint(e);
        setHasDrawn(true);
    };

    const move = (
        e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
    ) => {
        if (!drawingRef.current) return;
        e.preventDefault();
        const p = getPoint(e);
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx || !lastRef.current) return;
        ctx.beginPath();
        ctx.moveTo(lastRef.current.x, lastRef.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        lastRef.current = p;
    };

    const end = () => {
        drawingRef.current = false;
    };

    const clear = () => {
        const c = canvasRef.current;
        if (!c) return;
        c.getContext('2d')?.clearRect(0, 0, c.width, c.height);
        setHasDrawn(false);
    };

    const confirm = async () => {
        const c = canvasRef.current;
        if (!c) return;
        try {
            setSubmitting(true);
            setErr(null);
            const dataUrl = c.toDataURL('image/png');
            setSignatureDataUrl(dataUrl);
            const blob = dataUrlToBlob(dataUrl);
            await submitSignature(blob);
            setVerification({ signature: 'ok' });
            navigate('/onboarding/review');
        } catch (e) {
            setErr(e instanceof Error ? e.message : t.somethingWrong);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <MobileFrame
            progress={(6 / 7) * 100}
            showBack
            stepLabel={tpl(t.stepOf, { current: 6, total: 7 })}
        >
            <div
                className="fade-in"
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px 24px 28px',
                }}
            >
                <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>{t.signTitle}</h1>
                <p
                    style={{
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: 'var(--ink-500)',
                        marginBottom: 20,
                    }}
                >
                    {t.signSubtitle}
                </p>

                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: 220,
                        border: '1.5px dashed var(--ink-300)',
                        borderRadius: 12,
                        background: 'var(--surface)',
                        marginBottom: 12,
                        overflow: 'hidden',
                    }}
                >
                    <canvas
                        ref={canvasRef}
                        style={{
                            width: '100%',
                            height: '100%',
                            display: 'block',
                            touchAction: 'none',
                        }}
                        onMouseDown={start}
                        onMouseMove={move}
                        onMouseUp={end}
                        onMouseLeave={end}
                        onTouchStart={start}
                        onTouchMove={move}
                        onTouchEnd={end}
                    />
                    {!hasDrawn && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                                color: 'var(--ink-400)',
                            }}
                        >
                            <Icon name="signature" size={32} color="var(--ink-300)" />
                            <span style={{ fontSize: 13, marginTop: 8 }}>{t.signDrawHere}</span>
                        </div>
                    )}
                    <div
                        style={{
                            position: 'absolute',
                            left: 20,
                            right: 20,
                            bottom: 40,
                            height: 1,
                            background: 'var(--ink-200)',
                        }}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            left: 24,
                            bottom: 18,
                            fontSize: 10,
                            color: 'var(--ink-400)',
                            fontFamily: 'var(--font-mono)',
                        }}
                    >
                        TANDA TANGAN
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                    <button
                        onClick={clear}
                        disabled={!hasDrawn}
                        type="button"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '8px 14px',
                            borderRadius: 8,
                            background: 'var(--ink-100)',
                            color: 'var(--ink-700)',
                            fontSize: 13,
                            fontWeight: 600,
                            opacity: hasDrawn ? 1 : 0.4,
                        }}
                    >
                        <Icon name="trash" size={14} />
                        {t.signClear}
                    </button>
                </div>

                <p
                    style={{
                        fontSize: 12,
                        lineHeight: 1.5,
                        color: 'var(--ink-500)',
                        marginBottom: 20,
                        padding: '10px 12px',
                        background: 'var(--ink-50)',
                        borderRadius: 10,
                        display: 'flex',
                        gap: 8,
                    }}
                >
                    <Icon name="info" size={14} color="var(--ink-500)" />
                    {t.signHint}
                </p>

                {err && (
                    <div
                        className="chip chip-error"
                        style={{ marginBottom: 12, alignSelf: 'flex-start' }}
                    >
                        <Icon name="alert" size={14} /> {err}
                    </div>
                )}

                <div style={{ flex: 1 }} />

                <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    disabled={!hasDrawn || submitting}
                    onClick={confirm}
                >
                    {submitting ? <div className="spinner" /> : t.signConfirm}
                </button>
            </div>
        </MobileFrame>
    );
}
